import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { scheduleScrollHorizontalStripToActiveTab } from "@/lib/scroll-curriculum-tab-strip";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  KoreanWordsBlockingBody,
  KoreanWordsBodyPartsBody,
  KoreanWordsClassBody,
  KoreanWordsEtiquetteBody,
  KoreanWordsGeneralBody,
  KoreanWordsKarateFormsBody,
  KoreanWordsKickingBody,
  KoreanWordsKungFuWeaponsBody,
  KoreanWordsMooGongRyuBody,
  KoreanWordsNumbersBody,
  KoreanWordsPositionBody,
  KoreanWordsPunchingStrikingBody,
  KoreanWordsStancesBody,
  KoreanWordsStepWorkBody,
  KoreanWordsWTFBody,
  KoreanWordsWisdomBody,
} from "./KoreanWordsTab";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

/** Same inactive tab surface as Curriculum / Lil Dragons “Select Subject”. */
const KOREAN_WORDS_TAB_SURFACE = "!bg-white !text-gray-900";

const KOREAN_WORDS_SECTIONS = [
  { value: "numbers", label: "Numbers", mobileLabel: "Numbers", title: "Numbers (숫자)" },
  { value: "wisdom", label: "Wisdom", mobileLabel: "Wisdom", title: "Words of Wisdom (지혜의 말씀)" },
  { value: "etiquette", label: "Etiquette", mobileLabel: "Etiquette", title: "Etiquette (예절)" },
  { value: "general", label: "General", mobileLabel: "General", title: "General Terminology (일반 용어)" },
  { value: "class", label: "Class", mobileLabel: "Class", title: "Class Terminology (수업 용어)" },
  {
    value: "position",
    label: "Position",
    mobileLabel: "Position",
    title: "Position (직책 / 지위) and Rank (급 / 단)",
  },
  { value: "body", label: "Body", mobileLabel: "Body", title: "Body Parts (신체 부위)" },
  {
    value: "moogong",
    label: "MooGongRyu",
    mobileLabel: "MooGongRyu",
    title: "Moo Gong Ryu Wholistic Martial Arts Forms (전인무공 예술형)",
  },
  { value: "wt", label: "W.T.", mobileLabel: "W.T.", title: "Taekwondo (W.T.F.) Forms (태권도 품새)" },
  { value: "karate", label: "Karate", mobileLabel: "Karate", title: "Karate Forms (공수도 형)" },
  {
    value: "kungfu",
    label: "Kung Fu & Weapons",
    mobileLabel: "Kung Fu",
    title: "Wushu / Kung Fu and Weaponry (무술 / 쿵푸와 무기)",
  },
  { value: "step-work", label: "Step Work", mobileLabel: "Steps", title: "Step Work (Poom Balgi / 품밟기)" },
  { value: "kicking", label: "Kicking", mobileLabel: "Kicking", title: "Kicking Techniques (Chagi / 차기)" },
  { value: "blocking", label: "Blocking", mobileLabel: "Blocking", title: "Hand Blocking Techniques (Mahk-ki / 막기)" },
  {
    value: "striking",
    label: "Punching",
    mobileLabel: "Punching",
    title:
      "Punching (Ji-reugi / 지르기) & Striking (Dae-ri-gi / 때리기) Techniques",
  },
  { value: "stances", label: "Stances", mobileLabel: "Stances", title: "Stances (Jase / 자세)" },
] as const;

function KoreanWordsSubjectBody({
  value,
  placeholderLabel,
}: {
  value: string;
  placeholderLabel: string;
}) {
  switch (value) {
    case "numbers":
      return <KoreanWordsNumbersBody />;
    case "wisdom":
      return <KoreanWordsWisdomBody />;
    case "etiquette":
      return <KoreanWordsEtiquetteBody />;
    case "general":
      return <KoreanWordsGeneralBody />;
    case "class":
      return <KoreanWordsClassBody />;
    case "position":
      return <KoreanWordsPositionBody />;
    case "body":
      return <KoreanWordsBodyPartsBody />;
    case "moogong":
      return <KoreanWordsMooGongRyuBody />;
    case "wt":
      return <KoreanWordsWTFBody />;
    case "karate":
      return <KoreanWordsKarateFormsBody />;
    case "kungfu":
      return <KoreanWordsKungFuWeaponsBody />;
    case "step-work":
      return <KoreanWordsStepWorkBody />;
    case "kicking":
      return <KoreanWordsKickingBody />;
    case "blocking":
      return <KoreanWordsBlockingBody />;
    case "striking":
      return <KoreanWordsPunchingStrikingBody />;
    case "stances":
      return <KoreanWordsStancesBody />;
    default:
      return (
        <p className="text-gray-700 text-sm md:text-base">{placeholderLabel}</p>
      );
  }
}

export default function MembersKoreanWordsTab() {
  const [activeSection, setActiveSection] =
    useState<(typeof KOREAN_WORDS_SECTIONS)[number]["value"]>("numbers");
  const navScrollRef = useRef<HTMLDivElement | null>(null);
  const [showNavLeftHint, setShowNavLeftHint] = useState(false);
  const [showNavRightHint, setShowNavRightHint] = useState(false);

  const updateNavOverflowHints = useCallback(() => {
    const container = navScrollRef.current;
    if (!container) return;

    const { scrollLeft, clientWidth, scrollWidth } = container;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    }

    setShowNavLeftHint(container.scrollLeft > 4);
    setShowNavRightHint(maxScrollLeft - container.scrollLeft > 4);
  }, []);

  const scrollHorizontalTabStrip = useCallback(
    (stripRef: RefObject<HTMLDivElement | null>, direction: "backward" | "forward") => {
      const el = stripRef.current;
      if (!el) return;
      const delta = Math.max(160, Math.round(el.clientWidth * 0.65));
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const nextLeft =
        direction === "backward"
          ? Math.max(0, el.scrollLeft - delta)
          : Math.min(maxScrollLeft, el.scrollLeft + delta);
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    },
    [],
  );

  const scrollActiveSubjectIntoView = useCallback(() => {
    scheduleScrollHorizontalStripToActiveTab(
      navScrollRef.current,
      '[data-korean-words-subject-tab][data-state="active"]',
      () => updateNavOverflowHints(),
    );
  }, [updateNavOverflowHints]);

  useEffect(() => {
    updateNavOverflowHints();
    const handleResize = () => updateNavOverflowHints();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeSection, updateNavOverflowHints]);

  useLayoutEffect(() => {
    updateNavOverflowHints();
    requestAnimationFrame(() => updateNavOverflowHints());
  }, [activeSection, updateNavOverflowHints]);

  useEffect(() => {
    scrollActiveSubjectIntoView();
  }, [activeSection, scrollActiveSubjectIntoView]);

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold mb-6 text-taekwondo-red">Korean Words</h2>

      <p className="text-sm text-gray-600 mb-2">Select subject</p>
      <Tabs
        value={activeSection}
        onValueChange={(v) =>
          setActiveSection(v as (typeof KOREAN_WORDS_SECTIONS)[number]["value"])
        }
        className="w-full space-y-4"
      >
        <div className="relative border-b bg-white/95 backdrop-blur md:border-0 md:bg-transparent">
          <div
            ref={navScrollRef}
            onScroll={updateNavOverflowHints}
            className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            <TabsList className="inline-flex h-auto w-max min-w-0 flex-nowrap justify-start gap-2 rounded-full bg-gray-100 p-1">
              {KOREAN_WORDS_SECTIONS.map((c) => (
                <TabsTrigger
                  key={c.value}
                  value={c.value}
                  data-korean-words-subject-tab
                  className={cn(
                    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium border border-black/10 transition-shadow",
                    KOREAN_WORDS_TAB_SURFACE,
                    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
                  )}
                >
                  <span className="md:hidden">{c.mobileLabel}</span>
                  <span className="hidden md:inline">{c.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {showNavLeftHint && (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-14 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
              <button
                type="button"
                className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="Scroll Korean Words subjects left"
                onClick={() => scrollHorizontalTabStrip(navScrollRef, "backward")}
              >
                <ChevronsLeft className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          )}
          {showNavRightHint && (
            <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-14 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
              <button
                type="button"
                className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
                aria-label="Scroll Korean Words subjects right"
                onClick={() => scrollHorizontalTabStrip(navScrollRef, "forward")}
              >
                <ChevronsRight className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          )}
        </div>

        {KOREAN_WORDS_SECTIONS.map((c) => (
          <TabsContent
            key={c.value}
            value={c.value}
            className="mt-0 focus-visible:outline-none"
          >
            <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-taekwondo-red mb-4">{c.title}</h3>
              <KoreanWordsSubjectBody value={c.value} placeholderLabel={c.label} />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
